import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { MapPin, ChevronDown, ChevronLeft, ChevronRight, Cloud, Sun, Wind as WindIcon, Droplets, CloudRain, CloudSnow, Zap, LayoutDashboard, TrendingUp, BarChart3, Activity, LineChart as LineChartIcon, Map, Settings, TrendingDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchWeatherForecast } from '../../services/api';
import { WeatherForecast, MarketZone } from '../../types/data';
import { Logo } from '../Logo';
import { ViewType } from '../../hooks/useDashboardState';
// Location options (reuse from ForecastView)
interface LocationOption {
  name: string;
  code: MarketZone;
}
const availableLocations: LocationOption[] = [{
  name: 'Zurich, Switzerland',
  code: 'CH'
}, {
  name: 'Germany',
  code: 'DE'
}, {
  name: 'Spain',
  code: 'ES'
}, {
  name: 'United Kingdom',
  code: 'UK'
}, {
  name: 'France',
  code: 'FR'
}];
type AccordionSection = 'temperature' | 'wind' | 'solar';
interface WeatherCondition {
  icon: React.ReactNode;
  description: string;
  gradient: string;
}
const navItems: {
  icon: any;
  label: string;
  view: ViewType;
}[] = [{
  icon: LayoutDashboard,
  label: 'Overview',
  view: 'overview'
}, {
  icon: Zap,
  label: 'Oracle',
  view: 'oracle'
}, {
  icon: TrendingUp,
  label: 'Forecast',
  view: 'forecast'
}, {
  icon: BarChart3,
  label: 'Benchmarks',
  view: 'benchmarks'
}, {
  icon: Activity,
  label: 'Live Benchmarking',
  view: 'live-benchmarking'
}, {
  icon: LineChartIcon,
  label: 'Energy Market Aggregates',
  view: 'market-aggregates'
}, {
  icon: TrendingDown,
  label: 'Trading Strategies',
  view: 'trading-strategies'
}, {
  icon: Map,
  label: 'Map',
  view: 'map'
}, {
  icon: Settings,
  label: 'Settings',
  view: 'settings'
}];
// Weather visualization based on conditions
const getWeatherVisualization = (temp: number, windSpeed: number, solarRadiation: number, precipitation: number): WeatherCondition => {
  if (precipitation > 5) {
    if (temp < 0) {
      return {
        icon: <CloudSnow size={140} className="text-blue-400/80" strokeWidth={1.2} data-id="element-1606" />,
        description: 'Snow',
        gradient: 'from-blue-50/80 via-slate-50/60 to-white/40'
      };
    }
    return {
      icon: <CloudRain size={140} className="text-blue-500/80" strokeWidth={1.2} data-id="element-1607" />,
      description: 'Rain',
      gradient: 'from-blue-50/80 via-slate-50/60 to-white/40'
    };
  }
  if (windSpeed > 15) {
    return {
      icon: <WindIcon size={140} className="text-cyan-400/80" strokeWidth={1.2} data-id="element-1608" />,
      description: 'Windy',
      gradient: 'from-cyan-50/80 via-blue-50/60 to-white/40'
    };
  }
  if (solarRadiation > 600) {
    return {
      icon: <Sun size={140} className="text-amber-400/80" strokeWidth={1.2} data-id="element-1609" />,
      description: 'Sunny',
      gradient: 'from-amber-50/80 via-orange-50/60 to-white/40'
    };
  }
  if (solarRadiation > 300) {
    return {
      icon: <div className="relative" data-id="element-1610">
          <Cloud size={140} className="text-slate-300/80" strokeWidth={1.2} data-id="element-1611" />
          <Sun size={70} className="text-amber-300/80 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" strokeWidth={1.2} data-id="element-1612" />
        </div>,
      description: 'Partly Cloudy',
      gradient: 'from-slate-50/80 via-amber-50/40 to-white/40'
    };
  }
  return {
    icon: <Cloud size={140} className="text-slate-400/80" strokeWidth={1.2} data-id="element-1613" />,
    description: 'Cloudy',
    gradient: 'from-slate-50/80 via-slate-100/60 to-white/40'
  };
};
export function ForecastConceptView() {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeView, setActiveView] = useState<ViewType>('forecast');
  const [selectedLocation, setSelectedLocation] = useState<LocationOption>(availableLocations[0]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [forecasts, setForecasts] = useState<WeatherForecast[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [expandedSection, setExpandedSection] = useState<AccordionSection>('temperature');
  const chartContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    loadForecasts();
  }, [selectedLocation]);
  const loadForecasts = async () => {
    setLoading(true);
    const data = await fetchWeatherForecast(selectedLocation.code);
    setForecasts(data);
    setLastUpdated(new Date());
    setLoading(false);
  };
  const handleNavigation = (view: ViewType) => {
    if (view === 'forecast') {
      navigate('/dashboard');
    } else {
      setActiveView(view);
    }
  };
  // Get current conditions
  const currentForecast = forecasts.find(f => f.model === 'EPT-2');
  const currentConditions = currentForecast?.parameters || {
    temperature: 0,
    windSpeed: 0,
    solarRadiation: 0,
    precipitation: 0,
    cloudCover: 0
  };
  const weatherViz = getWeatherVisualization(currentConditions.temperature, currentConditions.windSpeed, currentConditions.solarRadiation, currentConditions.precipitation);
  // Prepare chart data
  const prepareChartData = (parameter: 'temperature' | 'windSpeed' | 'solarRadiation') => {
    const timePoints = [...new Set(forecasts.map(f => f.timestamp.toISOString()))];
    return timePoints.slice(0, 72).map(time => {
      const timestamp = new Date(time);
      const point: any = {
        time: timestamp.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }),
        timestamp: timestamp,
        date: timestamp.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        })
      };
      forecasts.filter(f => f.timestamp.toISOString() === time).forEach(f => {
        point[f.model] = f.parameters[parameter];
      });
      return point;
    });
  };
  // Prepare daily forecast
  const prepareDailyForecast = () => {
    const dailyData: any[] = [];
    const days = 7;
    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const dayForecasts = forecasts.filter(f => {
        const fDate = new Date(f.timestamp);
        return fDate.toDateString() === date.toDateString();
      });
      if (dayForecasts.length === 0) continue;
      const temps = dayForecasts.map(f => f.parameters.temperature);
      const winds = dayForecasts.map(f => f.parameters.windSpeed);
      const solar = dayForecasts.map(f => f.parameters.solarRadiation);
      dailyData.push({
        day: i === 0 ? 'Today' : date.toLocaleDateString('en-US', {
          weekday: 'long'
        }),
        date: date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        }),
        tempMin: Math.min(...temps),
        tempMax: Math.max(...temps),
        windAvg: winds.reduce((a, b) => a + b, 0) / winds.length,
        windMax: Math.max(...winds),
        solarAvg: solar.reduce((a, b) => a + b, 0) / solar.length,
        solarMax: Math.max(...solar)
      });
    }
    return dailyData;
  };
  const dailyForecast = prepareDailyForecast();
  const handleScrollLeft = () => {
    if (chartContainerRef.current) {
      chartContainerRef.current.scrollBy({
        left: -400,
        behavior: 'smooth'
      });
    }
  };
  const handleScrollRight = () => {
    if (chartContainerRef.current) {
      chartContainerRef.current.scrollBy({
        left: 400,
        behavior: 'smooth'
      });
    }
  };
  if (loading) {
    return <div className="flex items-center justify-center h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50" data-id="element-1614">
        <div className="relative" data-id="element-1615">
          <div className="animate-spin rounded-full h-16 w-16 border-[3px] border-slate-200 border-t-slate-900" data-id="element-1616" />
        </div>
      </div>;
  }
  const temperatureData = prepareChartData('temperature');
  const windData = prepareChartData('windSpeed');
  const solarData = prepareChartData('solarRadiation');
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 relative" data-id="element-1617">
      {/* Sidebar */}
      <motion.aside initial={false} animate={{
      width: sidebarCollapsed ? 80 : 260
    }} className="fixed left-0 top-0 h-screen bg-white/40 backdrop-blur-2xl border-r border-slate-200/60 z-50 flex flex-col" data-id="element-1618">
        {/* Logo */}
        <div className="h-20 flex items-center px-6 border-b border-slate-200/60" data-id="element-1619">
          {sidebarCollapsed ? <div className="w-8 h-8 flex items-center justify-center" data-id="element-1620">
              <span className="text-xl font-bold bg-gradient-to-br from-slate-900 to-slate-700 bg-clip-text text-transparent" data-id="element-1621">
                J
              </span>
            </div> : <Logo size="md" data-id="element-1622" />}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto" data-id="element-1623">
          {navItems.map(item => {
          const isActive = activeView === item.view;
          return <button key={item.view} onClick={() => handleNavigation(item.view)} className={`w-full flex items-center gap-3 py-3 rounded-xl transition-all duration-200 group relative overflow-hidden ${isActive ? 'bg-white/80 backdrop-blur-xl shadow-lg shadow-slate-900/5 ring-1 ring-slate-900/5 pl-4 pr-3' : 'text-slate-600 hover:text-slate-900 hover:bg-white/40 px-3'}`} data-id="element-1624">
                {isActive && <motion.div layoutId="activeNavIndicator" className="absolute left-0 top-0 bottom-0 w-1 bg-slate-900 rounded-r-full" transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-1625" />}
                <item.icon size={20} className={`relative z-10 transition-colors ${isActive ? 'text-slate-900' : 'text-slate-500 group-hover:text-slate-900'}`} strokeWidth={isActive ? 2.5 : 2} data-id="element-1626" />
                {!sidebarCollapsed && <span className={`font-medium text-sm truncate relative z-10 ${isActive ? 'text-slate-900' : ''}`} data-id="element-1627">
                    {item.label}
                  </span>}
              </button>;
        })}
        </nav>

        {/* Footer Toggle */}
        <div className="p-4 border-t border-slate-200/60" data-id="element-1628">
          <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="w-full flex items-center justify-center p-2.5 rounded-xl hover:bg-white/60 text-slate-600 hover:text-slate-900 transition-all" data-id="element-1629">
            <motion.div animate={{
            rotate: sidebarCollapsed ? 180 : 0
          }} transition={{
            duration: 0.2
          }} data-id="element-1630">
              <ChevronLeft size={20} strokeWidth={2} data-id="element-1631" />
            </motion.div>
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-[260px]'}`} data-id="element-1632">
        {/* Top Bar */}
        <div className="bg-white/40 backdrop-blur-2xl border-b border-slate-200/60 sticky top-0 z-40" data-id="element-1633">
          <div className="max-w-[1800px] mx-auto px-8 py-5" data-id="element-1634">
            <div className="flex items-center justify-between" data-id="element-1635">
              {/* Location Selector */}
              <div className="relative" data-id="element-1636">
                <button onClick={() => setShowLocationDropdown(!showLocationDropdown)} className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-white/60 backdrop-blur-xl border border-slate-200/60 hover:bg-white/80 hover:border-slate-300/60 text-slate-900 transition-all shadow-sm" data-id="element-1637">
                  <MapPin size={16} strokeWidth={2} data-id="element-1638" />
                  <span className="text-sm font-semibold" data-id="element-1639">
                    {selectedLocation.name}
                  </span>
                  <ChevronDown size={14} className={`transition-transform ${showLocationDropdown ? 'rotate-180' : ''}`} strokeWidth={2.5} data-id="element-1640" />
                </button>

                {showLocationDropdown && <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowLocationDropdown(false)} data-id="element-1641" />
                    <motion.div initial={{
                  opacity: 0,
                  y: -10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="absolute top-full left-0 mt-2 w-72 bg-white/95 backdrop-blur-2xl border border-slate-200/60 rounded-2xl shadow-2xl overflow-hidden z-50" data-id="element-1642">
                      {availableLocations.map(location => <button key={location.code} onClick={() => {
                    setSelectedLocation(location);
                    setShowLocationDropdown(false);
                  }} className={`w-full text-left px-5 py-3.5 text-sm transition-all ${selectedLocation.code === location.code ? 'bg-slate-100/80 text-slate-900 font-semibold' : 'text-slate-700 hover:bg-slate-50/80'}`} data-id="element-1643">
                          {location.name}
                        </button>)}
                    </motion.div>
                  </>}
              </div>

              {/* Date */}
              <div className="text-center" data-id="element-1644">
                <p className="text-sm font-semibold text-slate-900" data-id="element-1645">
                  {new Date().toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
                </p>
              </div>

              {/* Last Updated */}
              <div className="text-right" data-id="element-1646">
                <p className="text-xs font-medium text-slate-500" data-id="element-1647">
                  Updated{' '}
                  {Math.floor((Date.now() - lastUpdated.getTime()) / 60000)}m
                  ago
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-[1800px] mx-auto px-8 py-8" data-id="element-1648">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1649">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6" data-id="element-1650">
              {/* Hero Weather Card */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className={`bg-gradient-to-br ${weatherViz.gradient} backdrop-blur-2xl rounded-3xl p-10 shadow-xl border border-white/60 relative overflow-hidden`} data-id="element-1651">
                <div className="relative z-10 grid grid-cols-2 gap-12 items-center" data-id="element-1652">
                  <div data-id="element-1653">
                    <div className="mb-8" data-id="element-1654">
                      <div className="text-8xl font-bold text-slate-900 mb-3 tracking-tight" data-id="element-1655">
                        {Math.round(currentConditions.temperature)}°
                      </div>
                      <p className="text-3xl font-semibold text-slate-700" data-id="element-1656">
                        {weatherViz.description}
                      </p>
                    </div>

                    <div className="space-y-4" data-id="element-1657">
                      <div className="flex items-center gap-3" data-id="element-1658">
                        <div className="w-10 h-10 rounded-xl bg-white/60 backdrop-blur-xl flex items-center justify-center" data-id="element-1659">
                          <WindIcon size={18} className="text-slate-700" strokeWidth={2} data-id="element-1660" />
                        </div>
                        <span className="text-sm font-semibold text-slate-700" data-id="element-1661">
                          {currentConditions.windSpeed.toFixed(1)} m/s
                        </span>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-1662">
                        <div className="w-10 h-10 rounded-xl bg-white/60 backdrop-blur-xl flex items-center justify-center" data-id="element-1663">
                          <Sun size={18} className="text-slate-700" strokeWidth={2} data-id="element-1664" />
                        </div>
                        <span className="text-sm font-semibold text-slate-700" data-id="element-1665">
                          {Math.round(currentConditions.solarRadiation)} W/m²
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-center" data-id="element-1666">
                    <motion.div initial={{
                    scale: 0.8,
                    opacity: 0
                  }} animate={{
                    scale: 1,
                    opacity: 1
                  }} transition={{
                    delay: 0.2
                  }} data-id="element-1667">
                      {weatherViz.icon}
                    </motion.div>
                  </div>
                </div>
              </motion.div>

              {/* Accordion */}
              <div className="space-y-4" data-id="element-1668">
                <AccordionRow title="Temperature" isExpanded={expandedSection === 'temperature'} onToggle={() => setExpandedSection('temperature')} summary={`${Math.round(dailyForecast[0]?.tempMin || 0)}° - ${Math.round(dailyForecast[0]?.tempMax || 0)}°C`} icon={<Sun size={22} className="text-orange-500" strokeWidth={2} data-id="element-1670" />} data-id="element-1669">
                  <ChartSection data={temperatureData} dataKey="EPT-2" color="#ff006b" unit="°C" onScrollLeft={handleScrollLeft} onScrollRight={handleScrollRight} chartContainerRef={chartContainerRef} data-id="element-1671" />
                </AccordionRow>

                <AccordionRow title="Wind" isExpanded={expandedSection === 'wind'} onToggle={() => setExpandedSection('wind')} summary={`Avg: ${dailyForecast[0]?.windAvg.toFixed(1) || 0} m/s`} icon={<WindIcon size={22} className="text-cyan-500" strokeWidth={2} data-id="element-1673" />} data-id="element-1672">
                  <ChartSection data={windData} dataKey="EPT-2" color="#06b6d4" unit="m/s" onScrollLeft={handleScrollLeft} onScrollRight={handleScrollRight} chartContainerRef={chartContainerRef} data-id="element-1674" />
                </AccordionRow>

                <AccordionRow title="Solar Radiation" isExpanded={expandedSection === 'solar'} onToggle={() => setExpandedSection('solar')} summary={`Peak: ${Math.round(dailyForecast[0]?.solarMax || 0)} W/m²`} icon={<Sun size={22} className="text-amber-500" strokeWidth={2} data-id="element-1676" />} data-id="element-1675">
                  <ChartSection data={solarData} dataKey="EPT-2" color="#f59e0b" unit="W/m²" onScrollLeft={handleScrollLeft} onScrollRight={handleScrollRight} chartContainerRef={chartContainerRef} data-id="element-1677" />
                </AccordionRow>
              </div>
            </div>

            {/* Right Column */}
            <div className="lg:col-span-1" data-id="element-1678">
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} className="bg-white/40 backdrop-blur-2xl border border-slate-200/60 rounded-3xl p-6 shadow-xl sticky top-28" data-id="element-1679">
                <h3 className="text-lg font-bold text-slate-900 mb-5" data-id="element-1680">
                  {expandedSection === 'temperature' && '7-Day Forecast'}
                  {expandedSection === 'wind' && '7-Day Wind'}
                  {expandedSection === 'solar' && '7-Day Solar'}
                </h3>

                <div className="space-y-2" data-id="element-1681">
                  {dailyForecast.map((day, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.05
                }} className="flex items-center justify-between p-4 rounded-2xl bg-white/60 backdrop-blur-xl hover:bg-white/80 transition-all border border-slate-100/60" data-id="element-1682">
                      <div className="flex items-center gap-3" data-id="element-1683">
                        {expandedSection === 'temperature' && <Sun size={18} className="text-orange-500" strokeWidth={2} data-id="element-1684" />}
                        {expandedSection === 'wind' && <WindIcon size={18} className="text-cyan-500" strokeWidth={2} data-id="element-1685" />}
                        {expandedSection === 'solar' && <Sun size={18} className="text-amber-500" strokeWidth={2} data-id="element-1686" />}
                        <div data-id="element-1687">
                          <p className="text-sm font-semibold text-slate-900" data-id="element-1688">
                            {day.day}
                          </p>
                          <p className="text-xs font-medium text-slate-500" data-id="element-1689">
                            {day.date}
                          </p>
                        </div>
                      </div>

                      <div className="text-right" data-id="element-1690">
                        {expandedSection === 'temperature' && <p className="text-sm font-bold text-slate-900" data-id="element-1691">
                            {Math.round(day.tempMin)}° -{' '}
                            {Math.round(day.tempMax)}°
                          </p>}
                        {expandedSection === 'wind' && <div data-id="element-1692">
                            <p className="text-sm font-bold text-slate-900" data-id="element-1693">
                              {day.windAvg.toFixed(1)} m/s
                            </p>
                            <p className="text-xs font-medium text-slate-500" data-id="element-1694">
                              Peak: {day.windMax.toFixed(1)}
                            </p>
                          </div>}
                        {expandedSection === 'solar' && <div data-id="element-1695">
                            <p className="text-sm font-bold text-slate-900" data-id="element-1696">
                              {Math.round(day.solarMax)} W/m²
                            </p>
                            <p className="text-xs font-medium text-slate-500" data-id="element-1697">
                              Avg: {Math.round(day.solarAvg)}
                            </p>
                          </div>}
                      </div>
                    </motion.div>)}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
// Accordion Row Component
interface AccordionRowProps {
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  summary: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}
function AccordionRow({
  title,
  isExpanded,
  onToggle,
  summary,
  icon,
  children
}: AccordionRowProps) {
  return <motion.div layout className="bg-white/40 backdrop-blur-2xl border border-slate-200/60 rounded-3xl overflow-hidden shadow-xl" data-id="element-1698">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-7 hover:bg-white/60 transition-all" data-id="element-1699">
        <div className="flex items-center gap-4" data-id="element-1700">
          <div className="w-12 h-12 rounded-2xl bg-white/60 backdrop-blur-xl flex items-center justify-center" data-id="element-1701">
            {icon}
          </div>
          <div className="text-left" data-id="element-1702">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-1703">{title}</h3>
            {!isExpanded && <p className="text-sm font-medium text-slate-500 mt-1" data-id="element-1704">
                {summary}
              </p>}
          </div>
        </div>
        <ChevronDown size={22} className={`text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} strokeWidth={2} data-id="element-1705" />
      </button>

      <AnimatePresence data-id="element-1706">
        {isExpanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-1707">
            <div className="px-7 pb-7" data-id="element-1708">{children}</div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
// Chart Section
interface ChartSectionProps {
  data: any[];
  dataKey: string;
  color: string;
  unit: string;
  onScrollLeft: () => void;
  onScrollRight: () => void;
  chartContainerRef: React.RefObject<HTMLDivElement>;
}
function ChartSection({
  data,
  dataKey,
  color,
  unit,
  onScrollLeft,
  onScrollRight,
  chartContainerRef
}: ChartSectionProps) {
  return <div className="relative" data-id="element-1709">
      <button onClick={onScrollLeft} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2.5 bg-white/90 backdrop-blur-xl rounded-full shadow-lg border border-slate-200/60 hover:bg-white transition-all" data-id="element-1710">
        <ChevronLeft size={20} className="text-slate-700" strokeWidth={2.5} data-id="element-1711" />
      </button>
      <button onClick={onScrollRight} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2.5 bg-white/90 backdrop-blur-xl rounded-full shadow-lg border border-slate-200/60 hover:bg-white transition-all" data-id="element-1712">
        <ChevronRight size={20} className="text-slate-700" strokeWidth={2.5} data-id="element-1713" />
      </button>

      <div ref={chartContainerRef} className="overflow-x-auto scrollbar-hide px-14" style={{
      scrollbarWidth: 'none'
    }} data-id="element-1714">
        <div style={{
        minWidth: '1200px',
        height: '320px'
      }} data-id="element-1715">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1716">
            <AreaChart data={data} margin={{
            top: 10,
            right: 30,
            left: 0,
            bottom: 0
          }} data-id="element-1717">
              <defs data-id="element-1718">
                <linearGradient id={`gradient-${dataKey}`} x1="0" y1="0" x2="0" y2="1" data-id="element-1719">
                  <stop offset="5%" stopColor={color} stopOpacity={0.2} data-id="element-1720" />
                  <stop offset="95%" stopColor={color} stopOpacity={0} data-id="element-1721" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.04)" vertical={false} data-id="element-1722" />
              <XAxis dataKey="time" stroke="#cbd5e1" tick={{
              fill: '#64748b',
              fontSize: 11,
              fontWeight: 500
            }} tickLine={false} data-id="element-1723" />
              <YAxis stroke="#cbd5e1" tick={{
              fill: '#64748b',
              fontSize: 11,
              fontWeight: 500
            }} tickLine={false} label={{
              value: unit,
              angle: -90,
              position: 'insideLeft',
              style: {
                fill: '#64748b',
                fontSize: 12,
                fontWeight: 600
              }
            }} data-id="element-1724" />
              <Tooltip contentStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(226, 232, 240, 0.6)',
              borderRadius: '16px',
              padding: '12px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
            }} data-id="element-1725" />
              <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2.5} fill={`url(#gradient-${dataKey})`} animationDuration={1000} data-id="element-1726" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>;
}